import React, { useEffect, useMemo, useState } from "react";
import "./create-user.css";
import { userAPI, windfarmAPI } from "../../api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function errorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  return fallback;
}

// Staff invite a client: the invitee gets an email to choose a password and
// sees the wind farms selected here. Always scope wind-diagnostics; the
// backend rejects anything more for employees.
export default function CreateUserPage() {
  const [windfarms, setWindfarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");

  useEffect(() => {
    windfarmAPI
      .listAllWindfarms()
      .then((all) => setWindfarms([...all].sort((a, b) => a.name.localeCompare(b.name))))
      .catch((err) => setLoadError(errorMessage(err, "Could not load the wind farm list. Reload the page to try again.")))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return windfarms;
    return windfarms.filter((wf) => wf.name.toLowerCase().includes(q) || (wf.customer || "").toLowerCase().includes(q));
  }, [windfarms, search]);

  function toggle(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function validate() {
    if (!userName.trim()) return "First name is required.";
    if (!EMAIL_RE.test(email.trim())) return "Enter a valid email address.";
    if (selectedIds.size === 0) return "Select at least one wind farm.";
    return "";
  }

  function submit() {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSubmitting(true);
    userAPI
      .sendInvitation(userName.trim(), email.trim(), [...selectedIds], "wind-diagnostics")
      .then(() => {
        setSentTo(email.trim());
        setUserName("");
        setEmail("");
        setSearch("");
        setSelectedIds(new Set());
      })
      .catch((err) => setError(errorMessage(err, "Could not send the invitation.")))
      .finally(() => setSubmitting(false));
  }

  return (
    <div className="create-user-page">
      <div className="create-user-wrapper">
        <h1>Create user</h1>
        <p className="lede">
          Invite a client to Wind Diagnostics. They receive an email with a link to choose a password and will see the
          wind farms you select.
        </p>

        {loadError && <p className="notice error" role="alert">{loadError}</p>}
        {sentTo && (
          <p className="notice success" role="status">
            Invitation sent to {sentTo}.
          </p>
        )}

        {!loadError && (
          <form
            className="create-form"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <label>
              First name
              <input value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="e.g. Jane" />
            </label>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
            </label>

            <label>
              Wind farms ({selectedIds.size} selected)
              <input
                type="search"
                placeholder="Search by wind farm or customer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <div className="windfarm-rows" aria-busy={loading}>
              {loading && <p className="empty-state">Loading wind farms…</p>}
              {!loading && visible.length === 0 && <p className="empty-state">No wind farms match your search.</p>}
              {!loading &&
                visible.map((wf) => {
                  const on = selectedIds.has(wf.id);
                  return (
                    <button
                      key={wf.id}
                      type="button"
                      className={`windfarm-row ${on ? "on" : ""}`}
                      aria-pressed={on}
                      onClick={() => toggle(wf.id)}
                    >
                      <span className="info">
                        <strong>{wf.name}</strong>
                        {wf.customer && <span className="customer">{wf.customer}</span>}
                      </span>
                      <span className="switch" aria-hidden="true" />
                    </button>
                  );
                })}
            </div>

            {error && <p className="notice error" role="alert">{error}</p>}

            <button type="submit" className="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Send invitation"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
